import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLinkSettings, onChange } from '../helpers/onChange';
import { Editable, ReactEditor, Slate } from 'slate-react';
import { RenderSlateElement } from '../RenderSlateElement';
import {
  BaseMention,
  CustomEditor,
  CustomParagraph,
  SlateStateActions,
  SlateStateActionsWithValues,
  TransformType
} from '../../../../models/slate-editor';
import { BaseRange, Descendant, Transforms } from 'slate';
import { onKeyDown } from '../helpers/onKeyDown';
import { PostEditorContext } from '../../states/PostEditorProvider';
import {
  LinkDestination,
  LinkGoogleCallToAction,
  ThirdPartyProfileSearchType,
  useGetThirdPartyProfilesLazyQuery
} from '../../../../models/graphql';
import { Loader } from '../../../../components/containers/Loader';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { ListItemContainer } from '../../../../components/containers/ListItemContainer';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import classes from './SlateEditor.module.scss';
import classNames from 'classnames';
import { insertMention, removeMention } from '../helpers/withMentions';
import { debounce } from 'lodash';
import { PostEditorTextUpdateAction } from '../../states/PostEditorActions';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { showToast } from '../../../../helpers/showToast';
import { Portal } from '../../../../components/containers/Portal';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import {
  mapSelectedProfilesValues,
  SocialProfileWithBoardsAndMentionType
} from '../../helpers/mapSelectedProfilesValues';
import { SelectedSocialProfileValue } from '../../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { typeGuards } from '../helpers/typeGuards';
import { usePostLinkSettings } from '../../../../hooks/usePostLinkSettings';
import { RenderSlatePlaceholder } from '../RenderSlatePlaceholder';

export type SlateEditorViewProps = {
  //
  editor: CustomEditor;
  text: CustomParagraph[];
  id: number;
  isDragging: boolean;
};

export const SlateEditorView = (props: SlateEditorViewProps): JSX.Element => {
  const pastedRef = useRef<string | undefined>(undefined);
  const { text, id, editor, isDragging } = props;
  // Use a ref to keep track of the previous length of the undo stack
  const previousUndoLengthRef = useRef(editor.history.undos.length);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [getMentions, mentionsResult] = useGetThirdPartyProfilesLazyQuery();
  const mentionRef = useRef<HTMLDivElement | null>();
  const editorRef = useRef<CustomEditor>(editor);
  const [direction, setDirection] = useState<'left' | null>(null);
  const [target, setTarget] = useState<BaseRange | null>(null);
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [transformType, setTransformType] = useState<TransformType>(null);
  const [loading, setLoading] = useState(false);
  const { state: postEditorState, dispatch, profiles: availableProfiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const [updating, setUpdating] = useState<boolean>(false);
  const [pairedSocialProfile, setPairedSocialProfile] = useState<SocialProfileWithBoardsAndMentionType | null>(null);
  const postGroup = postEditorState.postGroups[postGroupIndex];
  const { customizeContentPerProfile, profiles: initialProfiles } = postGroup;
  const link =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? postGroup.posts[customizeIndex].content.link
      : postGroup.groupPostAttributes.content.link;

  const mappedProfileIds: string = JSON.stringify(initialProfiles, undefined, '');

  const socialProfiles: SelectedSocialProfileValue[] = useMemo(() => {
    return JSON.parse(mappedProfileIds);
  }, [mappedProfileIds]);

  const updateText = useMemo<SlateStateActions['updateText']>(
    () => (payload: PostEditorTextUpdateAction['payload']) => {
      dispatch({ type: 'textUpdate', payload });
    },
    [dispatch]
  );

  const linkSettingResult = usePostLinkSettings();
  const { defaultBitly, utmTracking, campaignTracking, shouldRemoveUtm } = linkSettingResult;

  const linkSettings: BaseLinkSettings = {
    destination: link?.destination ?? LinkDestination.INITIAL,
    useBitly: link && link.useBitly !== undefined ? link.useBitly : !!defaultBitly,
    googleCallToAction: link?.googleCallToAction ?? LinkGoogleCallToAction.NONE,
    utmTracking: link?.utmTracking ?? utmTracking,
    addUtm: link ? link.addUtm : campaignTracking,
    removeUtm: link ? link.removeUtm : shouldRemoveUtm,
    removePreview: link ? link.removePreview : false,
    linkMetadata: link ? link.linkMetadata : undefined
  };

  const slateActions: SlateStateActions = {
    setDirection,
    setTarget,
    setSearch,
    setIndex,
    setTransformType,
    setUpdating,
    updateText
  };

  const slateActionsWithValues: SlateStateActionsWithValues = {
    ...slateActions,
    direction,
    index,
    search,
    target,
    transformType,
    updating
  };

  const selectMention = (mention: BaseMention) => {
    if (!target) return;
    Transforms.select(editor, target);
    insertMention(editor, mention, target);
    setTarget(null);
    setTransformType(null);
  };

  const { loading: loadingMentions, data: mentionsData, error: mentionsResultError } = mentionsResult;
  const thirdPartyProfiles = useMemo(() => {
    return mentionsData?.searchThirdPartyProfiles ?? undefined;
  }, [mentionsData]);

  useEffect(() => {
    if (mentionsResultError) {
      setTarget(null);
      setLoading(false);
      triggerErrorToast('Error Fetching Mentions')(mentionsResultError);
    }
  }, [mentionsResultError]);

  useEffect(() => {
    const selectedProfiles = mapSelectedProfilesValues(socialProfiles, availableProfiles);
    if (selectedProfiles.length === 1 && selectedProfiles[0].mentionType) {
      setPairedSocialProfile(selectedProfiles[0]);
    } else if (
      selectedProfiles.length > 1 &&
      customizeContentPerProfile &&
      typeof customizeIndex === 'number' &&
      selectedProfiles[customizeIndex].mentionType
    ) {
      setPairedSocialProfile(selectedProfiles[customizeIndex]);
    } else {
      setPairedSocialProfile(null);
    }
  }, [customizeIndex, customizeContentPerProfile, socialProfiles, availableProfiles]);

  useEffect(() => {
    if ((!socialProfiles.length || socialProfiles.length > 1) && !customizeContentPerProfile && editorRef.current) {
      const shouldShowToast = removeMention(editorRef.current);
      if (shouldShowToast) {
        showToast({
          severity: 'warn',
          summary: 'Mention Removed',
          sticky: true,
          detail:
            'Mention(s) has been removed since you have selected multiple profiles. Switch to customize per profile if you wish to use mentions.'
        });
      }
    }
  }, [customizeContentPerProfile, socialProfiles]);

  //watch mentions
  useEffect(() => {
    if (target && transformType === 'mention' && search && pairedSocialProfile && pairedSocialProfile.mentionType) {
      const method = ThirdPartyProfileSearchType[pairedSocialProfile.mentionType];
      getMentions({
        variables: {
          term: search,
          socialProfileId: pairedSocialProfile.id,
          method
        }
      }).catch(e => {
        // TODO lazyQuery never throws an error. This error appears in result as apollo error, thus we need to watch it there.
        // this seems like an apollo bug
        setTarget(null);
        setLoading(false);
        triggerErrorToast('Error Fetching Mentions')(e);
      });
    }
  }, [search, target, transformType, pairedSocialProfile, getMentions]);

  useEffect(() => {
    setLoading(loadingMentions);
  }, [loadingMentions]);

  const onScroll = useCallback(
    (el: HTMLDivElement | null | undefined, editor: CustomEditor, target: BaseRange | null) => {
      if (!el || !target) return;
      try {
        const domRange = ReactEditor.toDOMRange(editor, target);
        const rect = domRange.getBoundingClientRect();
        el.style.top = rect.top + 24 <= 64 ? '64px' : `${rect.top + 24}px`;
        el.style.left = `${rect.left}px`;
      } catch (e) {
        // to dom range can fail if el reff and editor ar eout of sinc. if that happens
        // we just don't want to do a thing. The app should not brak. silent cach prevents it.
      }
    },
    []
  );

  /* post-editor-scrollable-content id is used in SlateEditor.view, since we could not pass ref. NOTE: don't change it */
  const scrollableContent = document.getElementById('post-editor-scrollable-content');

  const contentScroll = useCallback(() => {
    const { current: el } = mentionRef;
    onScroll(el, editor, target);
  }, [editor, onScroll, target]);

  //for mentions and links
  useEffect(() => {
    if (target && transformType === 'mention') {
      onScroll(mentionRef.current, editor, target);
      scrollableContent?.addEventListener('scroll', contentScroll);
    } else {
      scrollableContent?.removeEventListener('scroll', contentScroll);
    }
    return () => scrollableContent?.removeEventListener('scroll', contentScroll);
  }, [
    loadingMentions,
    editor,
    index,
    search,
    target,
    transformType,
    socialProfiles,
    onScroll,
    contentScroll,
    scrollableContent
  ]);

  // hide mentions dropdown if no mentions available
  useEffect(() => {
    if (thirdPartyProfiles?.length === 0) {
      setTarget(null);
    }
  }, [thirdPartyProfiles]);

  const profile: SocialProfileWithBoardsAndMentionType | null = useMemo(() => {
    const mappedSocialProfiles = mapSelectedProfilesValues(socialProfiles, availableProfiles);
    if (mappedSocialProfiles.length === 1) return mappedSocialProfiles[0];
    if (mappedSocialProfiles.length > 1 && customizeContentPerProfile && typeof customizeIndex === 'number')
      return mappedSocialProfiles[customizeIndex];
    return null;
  }, [socialProfiles, customizeIndex, customizeContentPerProfile, availableProfiles]);

  const mentionsList = thirdPartyProfiles?.flatMap((result, i) => {
    const method = pairedSocialProfile?.mentionType
      ? ThirdPartyProfileSearchType[pairedSocialProfile.mentionType]
      : null;
    const mention = {
      ...result,
      method
    };
    const profile = profilesHelpers.remapMentionTagsToSocialProfile(mention);
    if (!profile) return [];
    const itemClasses = classNames(classes.SocialProfileSelectorList, {
      [classes.SocialProfileSelectorListSelected]: i === index
    });
    return (
      <div
        key={`mention-key-${mention.externalId}-${i}`}
        className={itemClasses}
        onClick={() => selectMention(mention)}>
        <SocialProfile
          socialProfile={profile}
          size={'small'}
          label
          {...(mention.photoUrl ? { image: mention.photoUrl } : {})}
        />
      </div>
    );
  });

  if (!text || !editor) return <Loader variation={'dark'} size={'large'} />;
  const editorStyles: CSSProperties = isDragging
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical'
      }
    : {};

  return (
    <Slate
      editor={editor}
      value={text}
      onChange={debounce((v: Descendant[]) => {
        const pastedData = pastedRef.current;
        pastedRef.current = undefined;
        if (!typeGuards.isCustomParagraphArrayGuard(v)) throw new Error('Value is not a CustomParagraph[]');
        onChange(editor)(
          slateActions,
          {
            postGroupIndex,
            customizeIndex,
            customizeContentPerProfile,
            id,
            text: v
          },
          profile,
          linkSettings,
          direction,
          link?.initialUrl || null,
          pastedData,
          previousUndoLengthRef
        );
      }, 400)}>
      <Editable
        style={editorStyles}
        disabled={updating}
        placeholder={'Type your text here...'}
        renderPlaceholder={RenderSlatePlaceholder}
        renderElement={RenderSlateElement}
        onPaste={e => (pastedRef.current = e.clipboardData.getData('text'))}
        onKeyDown={event =>
          onKeyDown(event, editor)(
            slateActionsWithValues,
            thirdPartyProfiles?.map(mention => ({
              ...mention,
              method: pairedSocialProfile?.mentionType
                ? ThirdPartyProfileSearchType[pairedSocialProfile.mentionType]
                : null
            }))
          )
        }
      />
      {!!target && transformType === 'mention' && (
        <Portal>
          <div
            ref={localRef => (mentionRef.current = localRef)}
            className={classes.SlateWrapper}
            data-cy={'mentions-portal'}>
            <ListItemContainer type={'div'} expanded={!!mentionRef.current}>
              {loading ? <Loader variation={'dark'} size={'small'} /> : mentionsList}
            </ListItemContainer>
          </div>
        </Portal>
      )}
    </Slate>
  );
};
